import React, { useEffect, useState } from "react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core";
import { getCostCountAnalytics } from "../../../services/metricsApi";
import StatTile, { formatCompact } from "../StatTile";
import WarningBanner from "../WarningBanner";
import HorizontalBarChart from "../charts/HorizontalBarChart";
import "../Analytics.css";

const TABLE_COLUMNS = [
  { id: "plant", label: "Plant" },
  { id: "material", label: "Material" },
  { id: "total_count", label: "Count" },
  { id: "total_cost", label: "Total Cost (USD)" },
  { id: "highest_cost", label: "Highest Cost (USD)" },
  { id: "lowest_cost", label: "Lowest Cost (USD)" },
];

// §6 Cost & Count Analytics — the only status-agnostic dashboard, backed by
// Amount_in_USD__c (unlike every other dashboard, which sums raw Amount__c —
// Gap #3 in the metrics reference). Currency is hardcoded "(USD)" here only.
const CostCountSection = ({ id, filters, refreshKey }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    getCostCountAnalytics(filters)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [filters, refreshKey]);

  const { loading, data, error } = state;
  const kpi = data?.kpi_cards || {};
  const matrix = data?.count_matrix || [];

  const chartItems = matrix.map((row, idx) => ({
    key: `${row.plant}-${row.material}-${idx}`,
    label: `${row.plant} · ${row.material}`,
    value: row.total_cost || 0,
  }));

  const tableData = matrix.map((row, idx) => ({ ...row, _id: `${row.plant}-${row.material}-${idx}` }));

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">Cost &amp; Count Analytics</h2>
        <span className="analytics-section-caption">{matrix.length} plant × material rows</span>
      </div>

      <WarningBanner warning={data?.warning} />
      {error && <div className="overview-card-error">Failed to load Cost & Count Analytics: {error}</div>}
      {loading && !data && <div className="overview-card-loading">Loading…</div>}

      {data && (
        <>
          <div className="stat-tile-row">
            <StatTile label="Total Cost (USD)" value={formatCompact(kpi.total_cost?.value, { currency: true })} />
            <StatTile label="Total Count" value={formatCompact(kpi.total_count?.count)} />
            <StatTile label="Highest Cost (USD)" value={formatCompact(kpi.highest_cost?.value, { currency: true })} />
            <StatTile label="Lowest Cost (USD)" value={formatCompact(kpi.lowest_cost?.value, { currency: true })} />
          </div>

          <div className="analytics-section-card">
            <h4 className="analytics-section-title">Top Plants &amp; Materials by Cost</h4>
            <HorizontalBarChart
              items={chartItems}
              valueFormatter={(v) => formatCompact(v, { currency: true })}
              maxItems={15}
            />
          </div>

          <div className="analytics-section-card">
            <details className="analytics-collapsible" open={tableData.length <= 8}>
              <summary>Plant &times; Material Breakdown — {tableData.length} rows</summary>
              <McTable data={tableData} datakey="_id" zebrastripes truncate fit="small" columns={TABLE_COLUMNS} />
            </details>
          </div>
        </>
      )}
    </section>
  );
};

export default CostCountSection;
